import React from 'react';
import styled from 'styled-components';
import {
  Section,
  Inner,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  Divider,
  Grid,
  Card,
  Badge,
} from '../styles/v2.styled';
import aiFullstackPhoto from '../../image/instructors/instructor-ai-fullstack.png';
import dataBackendPhoto from '../../image/instructors/instructor-data-backend.png';
import vrarDesignPhoto from '../../image/instructors/instructor-vrar-design.png';
import aiDataPhoto from '../../image/instructors/instructor-ai-data.png';
import cloudSecurityPhoto from '../../image/instructors/instructor-cloud-security.png';
import productWeb3Photo from '../../image/instructors/instructor-product-web3.png';
import simulationTeamPhoto from '../../image/instructors/instructor-simulation-team-cyberpunk.png';

export const DEFAULT_INSTRUCTORS = [
  {
    id: 'default-ai-fullstack',
    user_id: 'admin',
    name: '윤수진',
    title: '대표 강사',
    specialty: 'Full-Stack / DevOps',
    bio: '15년 경력의 풀스택 개발자입니다. 실무 중심의 강의를 진행합니다.',
    photo_url: aiFullstackPhoto,
    email: 'admin@antsnest.co.kr',
    career: 'AI 서비스 설계, 풀스택 개발, DevOps 교육',
  },
  {
    id: 'default-data-backend',
    user_id: 'inst01',
    name: '김현준',
    title: '시니어 강사',
    specialty: 'React / TypeScript',
    bio: '대기업 프론트엔드 개발자 출신. UX와 컴포넌트 설계에 강합니다.',
    photo_url: dataBackendPhoto,
    email: 'kim@antsnest.co.kr',
    career: '프론트엔드 아키텍처와 데이터 기반 서비스 구축',
  },
  {
    id: 'default-vrar-design',
    user_id: 'inst02',
    name: '박서연',
    title: '백엔드 전문강사',
    specialty: 'Node.js / PostgreSQL',
    bio: '클라우드 백엔드 아키텍처 컨설턴트.',
    photo_url: vrarDesignPhoto,
    email: 'park@antsnest.co.kr',
    career: '백엔드 API, 데이터베이스, VR/AR 콘텐츠 연동',
  },
  {
    id: 'default-ai-data',
    user_id: 'inst03',
    name: '최민아',
    title: 'AI 데이터 강사',
    specialty: 'AI / Data Pipeline',
    bio: 'LLM 서비스와 데이터 파이프라인을 현업 프로젝트 방식으로 지도합니다.',
    photo_url: aiDataPhoto,
    email: 'choi@antsnest.co.kr',
    career: 'RAG, 데이터 처리 자동화, AI 서비스 운영',
  },
  {
    id: 'default-cloud-security',
    user_id: 'inst04',
    name: '이도윤',
    title: '클라우드 보안 강사',
    specialty: 'Cloud / Security',
    bio: '클라우드 인프라와 보안 운영을 실습 중심으로 설명합니다.',
    photo_url: cloudSecurityPhoto,
    email: 'lee@antsnest.co.kr',
    career: 'AWS, 컨테이너 보안, 관측성 기반 운영',
  },
  {
    id: 'default-product-web3',
    user_id: 'inst05',
    name: '정하린',
    title: '프로덕트 Web3 강사',
    specialty: 'UX / Web3',
    bio: '서비스 기획, UX 흐름, Web3 기능 설계를 함께 다룹니다.',
    photo_url: productWeb3Photo,
    email: 'jung@antsnest.co.kr',
    career: '프로덕트 설계, 지갑 연동 UX, 스마트컨트랙트 기초',
  },
];

const PHOTO_BY_USER = {
  admin: aiFullstackPhoto,
  inst01: dataBackendPhoto,
  inst02: vrarDesignPhoto,
  inst03: aiDataPhoto,
  inst04: cloudSecurityPhoto,
  inst05: productWeb3Photo,
  'simulation-campus': simulationTeamPhoto,
};

const PHOTO_BY_NAME = {
  윤수진: aiFullstackPhoto,
  안수지: aiFullstackPhoto,
  김현준: dataBackendPhoto,
  김도현: dataBackendPhoto,
  박서연: vrarDesignPhoto,
  최민아: aiDataPhoto,
  이도윤: cloudSecurityPhoto,
  정하린: productWeb3Photo,
};

const isMissingPhoto = (photoUrl) => (
  !photoUrl || /default\.png|\/static\/profile\//i.test(photoUrl)
);

export const normalizeInstructors = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return DEFAULT_INSTRUCTORS;

  const normalizedRows = rows.map((row, index) => {
    const fallback = DEFAULT_INSTRUCTORS[index % DEFAULT_INSTRUCTORS.length];
    const photo = isMissingPhoto(row.photo_url)
      ? PHOTO_BY_USER[row.user_id] || PHOTO_BY_NAME[row.name] || null
      : row.photo_url;

    return {
      ...fallback,
      ...row,
      photo_url: photo,
      specialty: row.specialty || fallback.specialty,
      bio: row.bio || fallback.bio,
      title: row.title || fallback.title,
    };
  });

  const registeredKeys = new Set(
    normalizedRows.map((row) => row.user_id || row.name || row.id)
  );
  const supplements = DEFAULT_INSTRUCTORS.filter(
    (instructor) => !registeredKeys.has(instructor.user_id) && !registeredKeys.has(instructor.name)
  );

  return [...normalizedRows, ...supplements].slice(0, 6);
};

const InstructorCard = styled(Card)`
  border-radius: 8px;
  box-shadow: none;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 34px rgba(17, 24, 39, 0.08);
  }
`;

const Photo = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  background: ${({ $url, theme }) => (
    $url
      ? `url(${$url}) center/cover no-repeat`
      : `linear-gradient(135deg, ${theme.colors.neutral[100]}, ${theme.colors.primary[100]})`
  )};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
`;

const Body = styled.div`
  padding: 22px 22px 24px;
`;

const Name = styled.h3`
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const Title = styled.div`
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.primary[700]};
  font-size: 0.86rem;
  font-weight: 800;
`;

const Bio = styled.p`
  min-height: 58px;
  margin: 12px 0 0;
  color: ${({ theme }) => theme.colors.neutral[600]};
  font-size: 0.9rem;
  line-height: 1.6;
`;

const Career = styled.p`
  margin: 12px 0 0;
  color: ${({ theme }) => theme.colors.neutral[500]};
  font-size: 0.82rem;
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const SmallBtn = styled.button`
  padding: 7px 12px;
  font-size: 0.8rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  background: ${({ $danger, theme }) => ($danger ? '#fee2e2' : theme.colors.neutral[0])};
  color: ${({ $danger, theme }) => ($danger ? '#b91c1c' : theme.colors.neutral[700])};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral[50]};
  }
`;

function InstructorSection({
  instructors,
  showAdminControls = false,
  onEdit,
  onRemove,
}) {
  const list = normalizeInstructors(instructors);

  return (
    <Section $py="72px" $pyMobile="48px">
      <Inner>
        <SectionHeader>
          <Divider $center />
          <SectionTitle>강사진</SectionTitle>
          <SectionSubtitle>총 {list.length}명의 강사가 등록되어 있습니다</SectionSubtitle>
        </SectionHeader>

        <Grid $cols={3} $colsTablet={2} $colsMobile={1}>
          {list.map((instructor) => (
            <InstructorCard key={instructor.id || instructor.user_id || instructor.name}>
              <Photo $url={instructor.photo_url} data-testid="instructor-photo" />
              <Body>
                <Name>{instructor.name}</Name>
                <Title>{instructor.title || '강사'}</Title>
                {instructor.specialty && <Badge $variant="primary">{instructor.specialty}</Badge>}
                <Bio>{instructor.bio}</Bio>
                {instructor.career && <Career>{instructor.career}</Career>}
                {showAdminControls && typeof instructor.id === 'number' && (
                  <Row>
                    <SmallBtn onClick={() => onEdit(instructor)}>수정</SmallBtn>
                    <SmallBtn $danger onClick={() => onRemove(instructor.id)}>삭제</SmallBtn>
                  </Row>
                )}
              </Body>
            </InstructorCard>
          ))}
        </Grid>
      </Inner>
    </Section>
  );
}

export default InstructorSection;
